<template>
  <div>
    <div class="auth-content" @keydown.enter="handleSubmit">
      <div class="auth-context left">
        <span class="auth-title">{{ localize('auth.page.otp.title') }}</span>
        <span
          v-if="otpData?.email"
          class="auth-subtitle"
          v-html="localizeWithValues('auth.page.otp.subtitle', [otpData.email])"
        />
      </div>
      <div class="otp-code-wrapper">
        <form @submit="handleSubmit">
          <gam-code v-bind="getGamCode" v-model="form.otp" />
        </form>
        <div class="otp-resend">
          <span class="label">{{ localize('auth.page.otp.code.label') }}</span>
          <span class="link" :class="{ disable: otpCountdown > 0 }" @click="sendAgain">
            {{ getLinkText() }}
          </span>
        </div>
      </div>
    </div>
    <div class="auth-methods-group">
      <div class="methods-row">
        <gam-button v-bind="getContinueButton" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { OtpMailData } from '@/core/data/auth/auth.interface';
import { RouterAuthType, RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { BaseUserInfo } from '@/core/data/user/user.interface';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import GamButton from '@/views/components/GamButton.vue';
import GamCode from '@/views/components/GamCode.vue';
import {
  GamButtonMessageVariant,
  GamButtonSize,
  GamButtonVariant,
} from '@/views/composables/constants/components/gamButton.constants';
import type { GamButtonMessageType, GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamCodeType } from '@/views/composables/models/components/GamCode';
import useValidate from '@vuelidate/core';
import { minLength, required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const router = useRouter();
const authStore = useAuthStore();
const { redirectAfterAuth } = storeToRefs(authStore);

const isLoading = ref<boolean>(false);
const errorMessage = ref<GamButtonMessageType | null>(null);

const otpData = ref<OtpMailData | null>(gambits.authService.getOtpMailData());
const otpCountdown = ref<number>(gambits.authService.getOtpSendDelay());

const otpSize = gambits.configService.getOtpSize();
const form = reactive({
  otp: '',
});
const validators = computed((): { otp: any } => {
  return {
    otp: { required, minLength: minLength(otpSize) },
  };
});
const v$ = useValidate(validators, form);

const route = useRoute();

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isValidOTP = (otp: string) => /^\d{4}$/.test(otp);
watch(
  () => [route.query.email, route.query.code],
  ([newEmail, newOtp]) => {
    const email = String(newEmail);
    const otp = String(newOtp);

    if (!otpData.value?.email && (!email || !isValidEmail(email))) {
      return router.push('/auth/email?message=link_broken');
    }

    if (!otpData.value?.email && (!otp || !isValidOTP(otp))) {
      errorMessage.value = {
        label: 'The link you followed expired. Try copy/pasting the code instead.',
        variant: GamButtonMessageVariant.ERROR,
      };

      return;
    }

    if (isValidEmail(email)) {
      otpData.value = { email: email, sendTime: Date.now() };
    }

    if (isValidOTP(otp)) {
      form.otp = otp;
    }

    nextTick(() => {
      setOtpCountdown();
      handleSubmit();
    });
  },
  { immediate: true },
);

watch(v$, (validator) => {
  if (validator.$invalid) {
    return;
  }

  handleSubmit();
});

const handleSubmit = (e?: Event) => {
  e?.preventDefault();
  e?.stopPropagation();

  if (form.otp.trim() === '' || !otpData.value?.email || otpData.value.email === '') return;

  submitCode(form.otp, otpData.value.email);
};

const setOtpCountdown = (): void => {
  setTimeout(() => {
    if (otpCountdown.value > 0) {
      otpCountdown.value -= 1;
      setOtpCountdown();
    }
  }, 1000);
};

const getLinkText = (): string => {
  if (otpCountdown.value <= 0) {
    return localize('auth.page.otp.code.link');
  } else {
    return localizeWithValues('auth.page.otp.code.retry', [otpCountdown.value.toString()]);
  }
};

const sendAgain = async (): Promise<void> => {
  if (otpCountdown.value <= 0 && otpData.value) {
    isLoading.value = true;
    errorMessage.value = null;
    const result = await gambits.authService.authWithEmailUsername(otpData.value.email);

    if (result.data) {
      await nextTick(() => {
        otpCountdown.value = gambits.authService.getOtpSendDelay();
        setOtpCountdown();
      });
    } else if (result.error) {
      errorMessage.value = {
        label: result.error.message,
        variant: GamButtonMessageVariant.ERROR,
      };
    }
    isLoading.value = false;
  }
};

const getGamCode = computed((): GamCodeType => {
  return {
    length: otpSize,
    autofocus: true,
    message: errorMessage.value,
    isError: !!errorMessage.value,
  };
});

const submitCode = async (otp: string, email: string) => {
  isLoading.value = true;
  errorMessage.value = null;
  const result = await gambits.authService.validateOtp({
    otp,
    emailOrUsername: email,
  });

  if (result.data) {
    const { data: userDetails } = result;

    authStore.setUser(userDetails);
    const { username, firstName, lastName, birthDate, rating } = userDetails.info as BaseUserInfo;

    if (userDetails.isActive) {
      router.push(redirectAfterAuth.value || { name: RouterNameType.SPARKS });
      authStore.clearRedirectPath();
      return;
    }

    if (!username || !firstName || !lastName || !birthDate) {
      return router.push({ name: RouterAuthType.AUTH_REGISTRATION });
    }

    if (!rating) {
      return router.push({ name: RouterAuthType.AUTH_RATINGS });
    }
  } else if (result.error) {
    errorMessage.value = {
      label: result.error.message,
      variant: GamButtonMessageVariant.ERROR,
    };
  }
  isLoading.value = false;
};

const getContinueButton = computed((): GamButtonType => {
  return {
    variant: GamButtonVariant.PRIMARY,
    size: GamButtonSize.LARGE,
    label: 'auth.page.otp.continue.button.label',
    isFullSize: true,
    isCentered: true,
    isDisabled: v$.value.otp.$invalid,
    isLoading: isLoading.value,
    onClick: async () => {
      handleSubmit();
    },
  };
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.auth-subtitle {
  font-size: 16px !important;
  font-weight: 300 !important;
  letter-spacing: 0 !important;
  color: var(--color-white-70) !important;
}

.auth-content {
  gap: calc(var(--spacing-menu) * 0.5) !important;

  .otp-code-wrapper {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: calc(var(--spacing-menu) * 0.5) !important;

    .otp-resend {
      display: flex;
      align-self: stretch;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      text-align: center;
      gap: var(--spacing-xs);

      .label {
        font-size: 16px;
        font-weight: 300;
        color: var(--color-white-70);
        line-height: 110%; /* 17.6px */
      }

      .link {
        font-size: 16px;
        font-weight: 700;
        line-height: 110%;
        background: var(--color-linear-gradient);
        background-clip: text;
        cursor: pointer;
        position: relative;
        transition: opacity var(--transition-fast);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;

        &.disable {
          opacity: 0.7;
          cursor: default;
          pointer-events: none;

          &:after {
            display: none;
          }
        }

        &:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 1px;
          background: var(--color-linear-gradient);
          transition: opacity var(--transition-fast);
          opacity: 0;
          left: 0;
          bottom: -2px;
        }

        @include hover {
          &:after {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
